/**
 * Imports in a studio draft (decision 3, board `sources-sheet` 6.2): a sheet
 * of a source the person brought, planned into one record or list. A list's
 * rows come in with the workspace at Build; a record's rows right after, as
 * an import job with Undo.
 *
 * The Build phase plans each import again against the real ids with the
 * import planner; these rules only keep a standing draft from planning what
 * cannot land.
 */
import { COLUMN_TRANSFORM_KINDS, type ColumnTransform } from '../imports';
import { STUDIO_LIMITS, type StudioDraft, type StudioImport } from './draft';
import type { StudioProblem } from './judge';

/** When an import's rows land: a list's with the build, a record's right after it. */
export const studioImportWhen = (imp: StudioImport, draft: StudioDraft): 'with_build' | 'after_build' =>
  draft.records.find((record) => record.key === imp.record)?.kind === 'directory' ? 'with_build' : 'after_build';

const SKIP_ROWS_MAX = 200;
const NOT_FILLABLE: ReadonlySet<string> = new Set(['formula', 'lines', 'attachment']);
const TRANSFORM_FITS: Readonly<Record<string, ReadonlySet<string>>> = {
  pick: new Set(['select', 'reference', 'multi_select']),
  date: new Set(['date', 'datetime']),
  number: new Set(['number', 'money', 'quantity']),
  money: new Set(['number', 'money', 'quantity']),
};

const wholeAtLeastZero = (value: number): boolean => Number.isInteger(value) && value >= 0;

/** Every problem with one import, all at once. Path and part are `import:<key>`. */
export const importProblems = (imp: StudioImport, draft: StudioDraft): StudioProblem[] => {
  const path = `import:${imp.key}`;
  const problems: StudioProblem[] = [];
  const say = (code: string, message: string): void => {
    problems.push({ code, message, path, part: path });
  };

  const source = draft.sources.find((one) => one.id === imp.source);
  if (source === undefined) say('IMPORT_SOURCE_UNKNOWN', 'The import names a source the draft does not hold.');
  else if (source.dismissed) say('IMPORT_SOURCE_DISMISSED', 'The import reads a source that was set aside.');
  const record = draft.records.find((one) => one.key === imp.record);
  if (record === undefined) say('IMPORT_RECORD_UNKNOWN', `"${imp.record}" is not a record or list of this draft.`);
  const sheet = imp.sheet.trim();
  if (sheet.length === 0 || sheet.length > 120) say('IMPORT_SHEET', 'A sheet is named in 1 to 120 characters.');
  if (
    (imp.headerRow !== null && !wholeAtLeastZero(imp.headerRow)) ||
    imp.skipRows.length > SKIP_ROWS_MAX ||
    imp.skipRows.some((row) => !wholeAtLeastZero(row))
  ) {
    say('IMPORT_HEADER_ROW', `The header row and the rows to skip are row numbers from 0, ${SKIP_ROWS_MAX} skipped rows at most.`);
  }
  if (imp.columns.length === 0 || imp.columns.length > STUDIO_LIMITS.importColumns) {
    say('IMPORT_COLUMNS', `An import maps 1 to ${STUDIO_LIMITS.importColumns} columns.`);
  }
  if (imp.rows !== undefined && !wholeAtLeastZero(imp.rows)) say('IMPORT_ROWS', 'The row count is a whole number.');
  for (const key of imp.unfilled ?? []) {
    if (record !== undefined && !record.fields.some((one) => one.key === key)) say('IMPORT_FIELD_UNKNOWN', `"${key}" is not a field of "${imp.record}".`);
  }

  const mapped = new Set<string>();
  for (const column of imp.columns) {
    const kind = column.transform.kind;
    if (!(COLUMN_TRANSFORM_KINDS as readonly string[]).includes(kind)) {
      say('IMPORT_TRANSFORM', `"${String(kind)}" is not a way a column is read.`);
    }
    if (column.column === null && kind !== 'constant') {
      say('IMPORT_COLUMN_MISSING', `"${column.header}" names no column, so only a fixed value can fill it.`);
    }
    if (column.field === null || record === undefined) continue;
    const field = record.fields.find((one) => one.key === column.field);
    if (field === undefined) {
      say('IMPORT_FIELD_UNKNOWN', `"${column.field}" is not a field of "${imp.record}".`);
      continue;
    }
    if (NOT_FILLABLE.has(field.type)) say('IMPORT_FIELD_NOT_FILLABLE', `"${column.field}" is not filled from a sheet.`);
    const fits = TRANSFORM_FITS[kind];
    if (fits !== undefined && !fits.has(field.type)) say('IMPORT_TRANSFORM', `A ${kind} column cannot fill "${column.field}".`);
    if (mapped.has(field.key)) say('IMPORT_FIELD_TWICE', `"${column.field}" is filled from two columns.`);
    mapped.add(field.key);
  }

  if (draft.imports.some((other) => other.key !== imp.key && other.source === imp.source && other.sheet === imp.sheet && other.record === imp.record)) {
    say('IMPORT_TWICE', 'The same sheet is imported into the same record twice.');
  }
  return problems;
};

/** The way a column fills a field of this type when the one it had no longer fits; null when no column fills it. */
export const studioTransformFor = (type: string, was: ColumnTransform): ColumnTransform | null => {
  if (NOT_FILLABLE.has(type)) return null;
  const fits = TRANSFORM_FITS[was.kind];
  if (fits === undefined || fits.has(type)) return was;
  if (TRANSFORM_FITS.pick!.has(type)) return { kind: 'pick' };
  if (TRANSFORM_FITS.date!.has(type)) return { kind: 'date', format: was.kind === 'date' ? was.format : 'DD-MM-YYYY' };
  if (TRANSFORM_FITS.number!.has(type)) return { kind: type === 'money' ? 'money' : 'number' };
  return { kind: 'text' };
};

/**
 * Imports kept in step with the records they fill (step 2.2 of
 * docs/plans/setup-file-data.md), on the records a batch reached: a column
 * into a field that is gone, or that can no longer be filled, is left out; a
 * field whose type changed takes the way its type is filled; a field filled
 * twice keeps its first column; a required field no longer there is no
 * longer waiting. A rename changes words only, so a renamed record or list
 * keeps its import. Answers the import paths it changed; `draft` is the
 * caller's own copy.
 */
export const mendStudioImports = (draft: StudioDraft, reached: (recordKey: string) => boolean): string[] => {
  const changed: string[] = [];
  for (const imp of draft.imports) {
    if (!reached(imp.record)) continue;
    const record = draft.records.find((one) => one.key === imp.record);
    if (record === undefined) continue;
    const before = JSON.stringify(imp);
    const used = new Set<string>();
    imp.columns = imp.columns.map((column) => {
      if (column.field === null) return column;
      const field = record.fields.find((one) => one.key === column.field);
      const transform = field === undefined || used.has(field.key) ? null : studioTransformFor(field.type, column.transform);
      if (field === undefined || transform === null) return column.column === null ? { ...column, field: null } : { ...column, field: null, transform: { kind: 'leave_out' } };
      used.add(field.key);
      return transform === column.transform ? column : { ...column, transform };
    });
    if (imp.unfilled !== undefined) {
      const unfilled = imp.unfilled.filter((key) => record.fields.some((one) => one.key === key && one.required === true) && !used.has(key));
      if (unfilled.length === 0) delete imp.unfilled;
      else imp.unfilled = unfilled;
    }
    if (JSON.stringify(imp) !== before) changed.push(`import:${imp.key}`);
  }
  return changed;
};
